import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Rocket, Users, Shield, Lock, Mail, AlertTriangle, ArrowRight } from "lucide-react";
import { RotatingRing } from "@/components/brand/Sigils";
import { IyafWordmark } from "@/components/brand/IyafMark";

function useMemberRoles() {
  const { t } = useTranslation();
  return [
    { key: "startup", icon: Rocket, color: "#FF9F1C", title: t("login.startupMember"), short: t("login.startupShort"), desc: t("login.startupDesc") },
    { key: "community", icon: Users, color: "#E01E84", title: t("login.communityMember"), short: t("login.communityShort"), desc: t("login.communityDesc") },
  ];
}

function LoginForm({ accent, testidPrefix }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setError(t("login.errorInvalid"));
    }, 900);
  };

  return (
    <form onSubmit={submit} className="rounded-3xl bg-white border border-black/5 p-6 sm:p-8" data-testid={`${testidPrefix}-form`}>
      <label className="block mb-4">
        <span className="text-xs uppercase tracking-widest text-iyaf-ink/55 font-semibold">{t("common.email")}</span>
        <div className="mt-2 relative">
          <Mail size={16} className="absolute start-3.5 top-1/2 -translate-y-1/2 text-iyaf-ink/40" />
          <input required type="email" data-testid={`${testidPrefix}-email`} value={email} onChange={(e) => { setEmail(e.target.value); setError(""); }} placeholder="you@example.com" className="w-full ps-10 pe-4 py-3 rounded-xl border border-black/10 focus:border-iyaf-blue outline-none" />
        </div>
      </label>
      <label className="block">
        <span className="text-xs uppercase tracking-widest text-iyaf-ink/55 font-semibold">{t("common.password")}</span>
        <div className="mt-2 relative">
          <Lock size={16} className="absolute start-3.5 top-1/2 -translate-y-1/2 text-iyaf-ink/40" />
          <input required type="password" data-testid={`${testidPrefix}-password`} value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }} placeholder="••••••••" className="w-full ps-10 pe-4 py-3 rounded-xl border border-black/10 focus:border-iyaf-blue outline-none" />
        </div>
      </label>

      <AnimatePresence>
        {error && (
          <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-4 p-3 rounded-xl bg-red-50 border border-red-100 flex items-center gap-2 text-sm text-red-700" data-testid={`${testidPrefix}-error`}>
            <AlertTriangle size={14} />
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      <button type="submit" disabled={loading} data-testid={`${testidPrefix}-submit`} className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-white font-semibold transition disabled:opacity-50" style={{ background: loading ? "#111827" : accent }}>
        {loading ? t("cta.signingIn") : t("cta.signIn")}
        {!loading && <ArrowRight size={16} />}
      </button>

      <div className="mt-4 flex justify-between text-xs">
        <button type="button" className="text-iyaf-ink/55 hover:text-iyaf-ink">{t("cta.forgotPassword")}</button>
        <Link to="/contact" className="text-iyaf-blue font-semibold hover:underline">{t("cta.needHelp")}</Link>
      </div>
    </form>
  );
}

export default function Login() {
  const { t } = useTranslation();
  const memberRoles = useMemberRoles();
  const [activeRole, setActiveRole] = useState("startup");
  const [staffOpen, setStaffOpen] = useState(false);
  const [hoveredRole, setHoveredRole] = useState(null);

  const accent = memberRoles.find((r) => r.key === activeRole)?.color || "#3663A8";

  return (
    <div className="relative min-h-[80vh] py-12 sm:py-16 overflow-hidden" data-testid="login-page">
      <RotatingRing size={760} color="rgba(54,99,168,0.08)" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden lg:block" />
      <RotatingRing size={520} color="rgba(54,99,168,0.12)" duration={120} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden lg:block" />

      <div className="container-iyaf relative">
        <div className="max-w-md mx-auto">
          <div className="flex justify-center mb-6">
            <IyafWordmark variant="main" size="lg" />
          </div>
          <h1 className="font-display font-black tracking-tighter leading-[0.95] text-center text-4xl sm:text-5xl mb-3">
            {t("login.title")} <span className="font-editorial italic font-normal text-iyaf-blue">{t("login.titleItalic")}</span>
          </h1>
          <p className="text-center text-iyaf-ink/60 mt-3">{t("login.subtitle")}</p>

          <div className="mt-8 grid grid-cols-2 gap-2 p-1.5 rounded-2xl bg-white border border-black/5" data-testid="role-toggle">
            {memberRoles.map((r) => {
              const Icon = r.icon;
              const isActive = activeRole === r.key;
              return (
                <div key={r.key} className="relative" onMouseEnter={() => setHoveredRole(r.key)} onMouseLeave={() => setHoveredRole(null)}>
                  <button type="button" onClick={() => setActiveRole(r.key)} data-testid={`role-${r.key}`} className={`w-full px-3 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all flex items-center justify-center gap-1.5 ${isActive ? "text-white" : "text-iyaf-ink/65 hover:text-iyaf-ink"}`} style={isActive ? { background: r.color } : {}}>
                    <Icon size={14} />
                    <span>{r.title}</span>
                  </button>
                  <AnimatePresence>
                    {hoveredRole === r.key && (
                      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }} transition={{ duration: 0.15 }} className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 z-30 pointer-events-none">
                        <div className="rounded-2xl bg-iyaf-ink text-white p-4 text-xs leading-relaxed shadow-2xl border border-white/10">
                          <p className="font-semibold mb-1" style={{ color: r.color }}>{r.short}</p>
                          <p className="text-white/80">{r.desc}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          <div className="mt-6">
            <LoginForm key={activeRole} accent={accent} testidPrefix="login" />
          </div>

          <div className="mt-10 flex items-center gap-3" data-testid="staff-separator">
            <div className="flex-1 h-px bg-black/10" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-iyaf-ink/40 font-mono">{t("login.staffSection")}</span>
            <div className="flex-1 h-px bg-black/10" />
          </div>

          <div className="mt-6 text-center" data-testid="staff-login-block">
            <p className="text-sm text-iyaf-ink/65 mb-3">{t("login.staffNote")}</p>
            <button type="button" onClick={() => setStaffOpen((v) => !v)} data-testid="staff-login-toggle" aria-expanded={staffOpen} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-iyaf-blue/30 text-iyaf-blue text-sm font-semibold hover:bg-iyaf-blue hover:text-white transition">
              <Shield size={14} /> {staffOpen ? t("login.closeStaffLogin") : t("login.staffLogin")}
            </button>

            <AnimatePresence>
              {staffOpen && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.25 }} className="mt-6 text-left overflow-hidden" data-testid="staff-login-panel">
                  <LoginForm accent="#3663A8" testidPrefix="staff-login" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
